import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotification } from '../contexts/NotificationContext';
import { apiService } from '../services/ApiService';
import '../styles/Home.css';
import '../styles/theme.css';
import Header from './Header';
import { FaBook, FaFileInvoiceDollar, FaChartPie, FaExclamationTriangle, FaMoneyBillWave } from 'react-icons/fa';
import { FormModal } from './ModalAlert';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, authLoading, logout } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const { showNotification } = useNotification();
  
  const [activeTab, setActiveTab] = useState('resumo');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [diarioObra, setDiarioObra] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    recentes: 0,
    concluidos: 0,
    valorTotal: 0,
    totalDiarioObra: 0,
    diarioObraConcluidas: 0,
    diarioObraEmAndamento: 0
  });
  const [resumoFinanceiro, setResumoFinanceiro] = useState(null);
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Efeito para gerenciar o contador
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown]);

  // Função para solicitar nova verificação de email
  const handleRequestEmailVerification = async () => {
    if (countdown > 0) return; // Não permite clicar se o contador estiver ativo

    try {
      setEmailVerificationLoading(true);
      const response = await apiService.solicitarVerificacaoEmail(currentUser.email, currentUser.nome);
      
      if (response.success) {
        setShowEmailVerificationModal(false);
        setCountdown(60); // Inicia o contador de 60 segundos
      }
    } catch (error) {
      console.error('Erro ao solicitar verificação de email:', error);
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  // Efeito para verificar autenticação e carregar dados
  useEffect(() => {
    const loadData = async () => {
      try {
        // Se ainda estiver carregando a autenticação, aguardar
        if (authLoading) {
          return;
        }

        // Se não houver usuário autenticado, redirecionar
        if (!currentUser) {
          navigate('/login', { replace: true });
          return;
        }

        // Verificar se o email está verificado
        if (!currentUser.emailVerificado) {
          setShowEmailVerificationModal(true);
        }

        // Carregar dados apenas se estiver autenticado
        setLoading(true);
        await Promise.all([
          loadProjects(),
          loadDiarioObra()
        ]);
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
        if (error.message === 'Não autenticado' || error.message === 'Sessão expirada') {
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, authLoading, navigate, logout]);

  // Função para carregar diário de obra
  const loadDiarioObra = async () => {
    try {
      setLoading(true);
      
      const result = await apiService.getDiarioObras();
      
      if (result.success && result.data?.obras) {
        // Ordenar obras por data de atualização (mais recentes primeiro)
        const obrasOrdenadas = result.data.obras
          .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
        
        // Pegar as 3 obras mais recentes
        const diarioObraRecentes = obrasOrdenadas.slice(0, 3);
        setDiarioObra(diarioObraRecentes);

        // Calcular estatísticas
        const totalDiarioObra = result.data.obras.length;
        const diarioObraConcluidas = result.data.obras.filter(obra => 
          obra.status?.toLowerCase() === 'concluída' || 
          obra.status?.toLowerCase() === 'concluido' ||
          obra.status?.toLowerCase() === 'concluído'
        ).length;
        const diarioObraEmAndamento = totalDiarioObra - diarioObraConcluidas;

        setStats(prevStats => ({
          ...prevStats,
          totalDiarioObra,
          diarioObraConcluidas,
          diarioObraEmAndamento
        }));
      } else {
        console.error('Erro ao carregar obras:', result.error);
        showNotification('error', 'Não foi possível carregar o diário de obras');
      }
    } catch (err) {
      console.error('Erro ao buscar diário de obra:', err);
      showNotification('error', 'Erro ao buscar diário de obra');
    } finally {
      setLoading(false);
    }
  };

  // Função para carregar projetos
  const loadProjects = async () => {
    try {
      setLoading(true);
      
      const result = await apiService.getOrcamentos();
      if (result.success && result.data && result.data.orcamentos) {
        setProjects(result.data.orcamentos);
        
        const now = new Date();
        const umMesAtras = new Date();
        umMesAtras.setMonth(now.getMonth() - 1);
        
        const projetosRecentes = result.data.orcamentos.filter(p => {
          const dataAtualizacao = new Date(p.updatedAt);
          return dataAtualizacao >= umMesAtras;
        });
        
        const projetosConcluidos = result.data.orcamentos.filter(p => 
          p.status?.toLowerCase() === 'finalizado' || p.status?.toLowerCase() === 'concluido'
        );
        
        const valorTotal = result.data.orcamentos.reduce((total, projeto) => {
          return total + (parseFloat(projeto.valorTotalGeral) || 0);
        }, 0);
        
        setStats({
          total: result.data.orcamentos.length,
          recentes: projetosRecentes.length,
          concluidos: projetosConcluidos.length,
          valorTotal: valorTotal,
          totalDiarioObra: 0,
          diarioObraConcluidas: 0,
          diarioObraEmAndamento: 0
        });
      } else {
        showNotification('error', 'Não foi possível carregar os orçamentos');
      }
    } catch (err) {
      showNotification('error', 'Erro ao buscar dados');
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar o resumo financeiro
  const loadResumoFinanceiro = async () => {
    setLoadingResumo(true);
    try {
      const hoje = new Date();
      
      // Garantir que estamos usando o mês atual
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      
      // Garantir que não estamos usando datas futuras
      if (fimMes > hoje) {
        fimMes.setTime(hoje.getTime());
      }

      const dataInicio = inicioMes.toISOString().split('T')[0];
      const dataFim = fimMes.toISOString().split('T')[0];
      
      console.log('Carregando resumo financeiro para o período:', { dataInicio, dataFim });
      
      // Usar o mesmo método que a página de ControleCustos
      const response = await apiService.getCustosFiltrados({
        tipo: 'todos',
        categoria: 'todos',
        dataInicio,
        dataFim
      });
      
      if (response.success) {
        // Processar os dados para criar o resumo
        let transacoesData = [];
        
        // Verificar diferentes possíveis estruturas na resposta
        if (response.data.transacoes) {
          transacoesData = response.data.transacoes;
        } else if (response.data.data && response.data.data.transacoes) {
          transacoesData = response.data.data.transacoes;
        } else if (Array.isArray(response.data)) {
          transacoesData = response.data;
        }

        // Calcular o resumo financeiro
        const resumo = {
          saldo: 0,
          totalEntradas: 0,
          totalSaidas: 0,
          resumoPorCategoria: {},
          resumoPorEntidade: {}
        };

        // Processar cada transação
        transacoesData.forEach(transacao => {
          const valor = parseFloat(transacao.valor) || 0;
          
          // Atualizar totais
          if (transacao.tipo === 'entrada') {
            resumo.totalEntradas += valor;
            resumo.saldo += valor;
          } else {
            resumo.totalSaidas += valor;
            resumo.saldo -= valor;
          }

          // Resumo por categoria
          if (transacao.categoria) {
            if (!resumo.resumoPorCategoria[transacao.categoria]) {
              resumo.resumoPorCategoria[transacao.categoria] = 0;
            }
            resumo.resumoPorCategoria[transacao.categoria] += valor;
          }

          // Resumo por entidade
          if (transacao.entidadeNome) {
            if (!resumo.resumoPorEntidade[transacao.entidadeNome]) {
              resumo.resumoPorEntidade[transacao.entidadeNome] = 0;
            }
            resumo.resumoPorEntidade[transacao.entidadeNome] += valor;
          }
        });

        setResumoFinanceiro(resumo);
      } else {
        console.error('Erro ao carregar resumo financeiro:', response.error);
        setResumoFinanceiro(null);
        if (response.error === 'Transação não encontrada') {
          showNotification('info', 'Não há transações registradas para o período atual.');
        } else {
          showNotification('error', 'Erro ao carregar resumo financeiro: ' + (response.error || 'Erro desconhecido'));
        }
      }
    } catch (err) {
      console.error('Exceção ao carregar resumo financeiro:', err);
      setResumoFinanceiro(null);
      showNotification('error', 'Erro ao carregar resumo financeiro: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setLoadingResumo(false);
    }
  };

  // Buscar resumo ao selecionar a aba
  useEffect(() => {
    if (activeTab === 'resumo') {
      loadResumoFinanceiro();
    }
  }, [activeTab]);

  const removerCaracteresEspeciais = (texto) => {
    return texto
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toLowerCase();
  };
  
  const handleSelectProject = (project) => {
    const clienteNormalizado = project.cliente
      ? removerCaracteresEspeciais(project.cliente.trim())
      : 'cliente';
    
    navigate(`/orcamento/${clienteNormalizado}`);
  };

  const handleNavigateToDiarioObra = () => {
    navigate('/diario-obra');
  };

  const handleNavigateToControleCustos = () => {
    navigate('/controle-custos');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não disponível';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR').format(date);
  };

  return (
    <div id="home-page" className={`home-container ${darkMode ? 'dark-mode' : ''}`}>
      <Header 
        onLogout={logout}
        onAction={(action, message) => {
          if (action === 'info') {
            showNotification('info', message);
          }
        }}
      />

      {/* Modal de verificação de email */}
      {showEmailVerificationModal && (
        <div className="modal-verification">
          <div className="modal-verification-content">
            <div className="modal-verification-header">
              <h2>Verificação de Email Necessária</h2>
            </div>
            <div className="modal-verification-body">
              <div className="email-verification-modal">
                <FaExclamationTriangle className="warning-icon" />
                <div className="verification-message">
                  <p>Para garantir a segurança da sua conta e acessar todas as funcionalidades do sistema, por favor verifique seu email.</p>
                  <p className="email-highlight">Um email de verificação foi enviado para:</p>
                  <div className="email-box">
                    <i className="fas fa-envelope"></i>
                    <strong>{currentUser?.email}</strong>
                  </div>
                  <p className="verification-note">Se você não recebeu o email, verifique sua pasta de spam ou clique no botão abaixo para solicitar um novo email de verificação.</p>
                </div>
              </div>
            </div>
            <div className="modal-verification-footer">
              <button 
                className="modal-verification-button secondary"
                onClick={() => setShowEmailVerificationModal(false)}
              >
                <i className="fas fa-times"></i>
                Fechar
              </button>
              <button 
                className={`modal-verification-button primary ${countdown > 0 ? 'disabled' : ''}`}
                onClick={handleRequestEmailVerification}
                disabled={emailVerificationLoading || countdown > 0}
              >
                {emailVerificationLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Enviando...
                  </>
                ) : countdown > 0 ? (
                  <>
                    <i className="fas fa-clock"></i>
                    Aguarde {countdown}s
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i>
                    Solicitar Novo Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="home-content">
        <div className="welcome-container">
          {loading ? (
            <div className="loading-container">
              <i className="fas fa-spinner spin-animation" style={{ fontSize: '2rem', color: 'var(--primary-color)' }}></i>
              <p>Carregando seus projetos...</p>
            </div>
          ) : (
            <>
              <div className="welcome-header">
                <h2>Bem-vindo(a), {currentUser?.nome?.split(' ')[0] || 'Usuário'}!</h2>
                {!currentUser?.emailVerificado && (
                  <div className="email-verification-warning">
                    <FaExclamationTriangle />
                    <span>Seu email ainda não foi verificado</span>
                  </div>
                )}
              </div>

              <div className="tabs-container">
                <div 
                  className={`menu-item ${activeTab === 'resumo' ? 'active' : ''} ${!currentUser?.emailVerificado ? 'disabled' : ''}`}
                  onClick={() => currentUser?.emailVerificado && setActiveTab('resumo')}
                >
                  <FaChartPie className="menu-icon" />
                  <span>Resumo Financeiro</span>
                </div>
                <div 
                  className={`menu-item ${activeTab === 'orcamentos' ? 'active' : ''} ${!currentUser?.emailVerificado ? 'disabled' : ''}`}
                  onClick={() => currentUser?.emailVerificado && setActiveTab('orcamentos')}
                >
                  <FaFileInvoiceDollar className="menu-icon" />
                  <span>Orçamentos</span>
                </div>
                <div 
                  className={`menu-item ${activeTab === 'diario-obra' ? 'active' : ''} ${!currentUser?.emailVerificado ? 'disabled' : ''}`} 
                  onClick={() => {
                    if (currentUser?.emailVerificado) {
                      setActiveTab('diario-obra');
                      loadDiarioObra();
                    }
                  }}
                >
                  <FaBook className="menu-icon" />
                  <span>Diário de Obras</span>
                </div>
              </div>

              {/* Conteúdo das tabs */}
              {!currentUser?.emailVerificado ? (
                <div className="email-verification-content">
                  <FaExclamationTriangle className="warning-icon-large" />
                  <h3>Verificação de Email Necessária</h3>
                  <p>Para acessar todas as funcionalidades do sistema, por favor verifique seu email.</p>
                  <button 
                    className={`verify-email-button ${countdown > 0 ? 'disabled' : ''}`}
                    onClick={handleRequestEmailVerification}
                    disabled={emailVerificationLoading || countdown > 0}
                  >
                    {emailVerificationLoading ? 'Enviando...' : 
                     countdown > 0 ? `Aguarde ${countdown}s` : 
                     'Solicitar Novo Email de Verificação'}
                  </button>
                </div>
              ) : (
                <>
                  {activeTab === 'resumo' && (
                    <div className="resumo-financeiro-container">
                      <div className="resumo-header">
                        <h2>Resumo Financeiro</h2>
                        <button 
                          className="btn-controle-custos"
                          onClick={handleNavigateToControleCustos}
                          title="Ir para Controle de Custos"
                        >
                          <FaMoneyBillWave />
                          <span>Controle de Custos</span>
                        </button>
                      </div>
                      {loadingResumo ? (
                        <div className="loading-container">
                          <i className="fas fa-spinner spin-animation" style={{ fontSize: '2rem', color: 'var(--primary-color)' }}></i>
                          <p>Carregando resumo financeiro...</p>
                        </div>
                      ) : resumoFinanceiro ? (
                        <>
                          <div className="resumo-saldos">
                            <div className="resumo-saldo-card saldo-atual">
                              <span>Saldo Atual</span>
                              <strong>{formatCurrency(resumoFinanceiro.saldo)}</strong>
                            </div>
                            <div className="resumo-saldo-card entradas">
                              <span>Total de Entradas</span>
                              <strong>{formatCurrency(resumoFinanceiro.totalEntradas || resumoFinanceiro.entradas)}</strong>
                            </div>
                            <div className="resumo-saldo-card saidas">
                              <span>Total de Saídas</span>
                              <strong>{formatCurrency(resumoFinanceiro.totalSaidas || resumoFinanceiro.saidas)}</strong>
                            </div>
                          </div>
                          {resumoFinanceiro.resumoPorCategoria && Object.keys(resumoFinanceiro.resumoPorCategoria).length > 0 && (
                            <div className="resumo-categorias">
                              <h3>Por Categoria</h3>
                              <ul>
                                {Object.entries(resumoFinanceiro.resumoPorCategoria).map(([cat, val]) => (
                                  <li key={cat}><strong>{cat}:</strong> {formatCurrency(val)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {resumoFinanceiro.resumoPorEntidade && Object.keys(resumoFinanceiro.resumoPorEntidade).length > 0 && (
                            <div className="resumo-entidades">
                              <h3>Por Entidade</h3>
                              <ul>
                                {Object.entries(resumoFinanceiro.resumoPorEntidade).map(([ent, val]) => (
                                  <li key={ent}><strong>{ent}:</strong> {formatCurrency(val)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="empty-state">
                          <i className="fas fa-chart-line" style={{ fontSize: '3rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}></i>
                          <h3>Nenhum dado financeiro disponível</h3>
                          <p>Não há transações registradas para o período atual.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'orcamentos' && (
                    <>
                      <div className="stats-container">
                        <div className="stat-card">
                          <div className="stat-icon"><i className="fas fa-list"></i></div>
                          <div className="stat-info">
                            <h3>{stats.total}</h3>
                            <p>Orçamentos Totais</p>
                          </div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon"><i className="fas fa-clock"></i></div>
                          <div className="stat-info">
                            <h3>{stats.recentes}</h3>
                            <p>Orçamentos Recentes</p>
                          </div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon"><i className="fas fa-check"></i></div>
                          <div className="stat-info">
                            <h3>{stats.concluidos}</h3>
                            <p>Orçamentos Concluídos</p>
                          </div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon"><i className="fas fa-dollar-sign"></i></div>
                          <div className="stat-info">
                            <h3>{formatCurrency(stats.valorTotal)}</h3>
                            <p>Valor Total</p>
                          </div>
                        </div>
                      </div>

                      <div className="action-container">
                        <button 
                          className="action-button create-button" 
                          onClick={() => navigate('/orcamentos')}
                        >
                          <i className="fas fa-file-invoice-dollar"></i>
                          Gerenciar Orçamentos
                        </button>
                      </div>

                      {projects && projects.length > 0 ? (
                        <div className="recent-projects">
                          <h3>Orçamentos Recentes</h3>
                          <div className="projects-grid">
                            {projects.slice(0, 5).map(project => (
                              <div 
                                key={project.id} 
                                className="project-card"
                                onClick={() => handleSelectProject(project)}
                              >
                                <h4>{project.name || project.cliente}</h4>
                                <p className="project-description">{project.descricao || 'Sem descrição'}</p>
                                <div className="project-meta">
                                  <span>Atualizado: {formatDate(project.updatedAt)}</span>
                                  <span>{formatCurrency(project.valorTotalGeral || 0)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="empty-projects">
                          <i className="fas fa-folder-open" style={{ fontSize: '3rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}></i>
                          <h3>Nenhum orçamento encontrado</h3>
                        </div>
                      )}
                    </>
                  )}

                  {activeTab === 'diario-obra' && (
                    <div className="diario-obra-container">
                      <div className="stats-container">
                        <div className="stat-card">
                          <div className="stat-icon"><i className="fas fa-hard-hat"></i></div>
                          <div className="stat-info">
                            <h3>{stats.totalDiarioObra}</h3>
                            <p>Total de Obras</p>
                          </div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
                          <div className="stat-info">
                            <h3>{stats.diarioObraConcluidas}</h3>
                            <p>Obras Concluídas</p>
                          </div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon"><i className="fas fa-tools"></i></div>
                          <div className="stat-info">
                            <h3>{stats.diarioObraEmAndamento}</h3>
                            <p>Obras em Andamento</p>
                          </div>
                        </div>
                      </div>

                      <div className="action-container">
                        <button 
                          className="action-button create-button" 
                          onClick={handleNavigateToDiarioObra}
                        >
                          <i className="fas fa-hard-hat"></i>
                          Gerenciar Obras
                        </button>
                      </div>

                      <h3>Últimas Obras</h3>
                      {diarioObra.length > 0 ? (
                        <div className="diario-obra-grid">
                          {diarioObra.map(obra => (
                            <div 
                              key={obra.id} 
                              className="obra-card"
                              onClick={() => navigate(`/diario-obra/${obra.id}`)}
                            >
                              <div className="obra-image-container">
                                {obra.imagem ? (
                                  <img 
                                    src={obra.imagem} 
                                    alt={obra.nome} 
                                    className="obra-image"
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/300x200?text=Sem+Imagem';
                                    }}
                                  />
                                ) : (
                                  <div className="obra-image-placeholder">
                                    <i className="fas fa-hard-hat"></i>
                                  </div>
                                )}
                                <div className="obra-status-badge">
                                  <span className={`status-badge ${obra.status?.toLowerCase()}`}>
                                    {obra.status || 'Em andamento'}
                                  </span>
                                </div>
                              </div>
                              <div className="obra-content">
                                <h4>{obra.nome || 'Obra sem nome'}</h4>
                                <p className="obra-endereco">{obra.endereco || 'Endereço não informado'}</p>
                                <div className="obra-info">
                                  <div className="obra-engenheiro">
                                    <i className="fas fa-user-tie"></i>
                                    <span>{obra.responsavel || 'Responsável não definido'}</span>
                                  </div>
                                  <div className="obra-data">
                                    <i className="fas fa-calendar-alt"></i>
                                    <span>{formatDate(obra.createdAt) || 'Data não disponível'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="empty-diario-obra">
                          <i className="fas fa-hard-hat" style={{ fontSize: '3rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}></i>
                          <h3>Nenhuma obra encontrada</h3>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

        <footer className="home-footer">
          <p>&copy; {new Date().getFullYear()} ObrasWise - Todos os direitos reservados</p>
        </footer>
    </div>
  );
};

export default Home; 